import { useEffect, useMemo, useState } from 'react';
import { useController } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Plus, Search } from 'lucide-react';
import type { RoomAreaListItem } from '@kreart/shared';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useFormContext } from '@/components/form';
import { useFieldError } from '@/components/form/utils';
import { useAreas } from '../hooks';

interface AreaSearchFieldProps {
  name: string;
  label: string;
  projectId: string;
  /** The currently selected room. Areas are scoped to it; empty disables the field. */
  roomId: string | undefined;
  placeholder?: string;
  /** Opens the parent's create-area dialog (scoped to `roomId`). */
  onCreateNew?: () => void;
}

/**
 * Client-style combobox for picking a {@link RoomAreaListItem} within a room.
 *
 * Mirrors `ClientSearchField`: an input that opens a `role="listbox"` panel with
 * a distinctly-styled "+ New area" row at the top. Areas per room are few, so the
 * list is fetched whole and filtered client-side (no debounce). The field is
 * disabled until a room is selected.
 */
export function AreaSearchField({
  name,
  label,
  projectId,
  roomId,
  placeholder,
  onCreateNew,
}: AreaSearchFieldProps) {
  const { t } = useTranslation();
  const { clearErrors, trigger } = useFormContext();
  const {
    field,
    fieldState: { error },
  } = useController({ name });
  const errorMessage = useFieldError(name);
  // `typedValue` is the user's filter text while typing; `null` means "not
  // typing — show the selected area's name". The displayed value is DERIVED
  // (see `displayValue` below) rather than stored-then-cleared by effects, which
  // avoids a mount-time race where a "clear on room change" effect would wipe the
  // prefilled area name on edit (the id stayed set, so it saved but showed blank).
  const [typedValue, setTypedValue] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const selectedId = typeof field.value === 'string' ? field.value : '';

  const areasQuery = useAreas(projectId, roomId);
  const areas = useMemo(() => areasQuery.data ?? [], [areasQuery.data]);
  const selectedArea = useMemo(() => areas.find((a) => a.id === selectedId), [areas, selectedId]);

  // Stop showing stale filter text when the room changes; the derived display
  // then reflects the new room's selection (usually none).
  useEffect(() => {
    setTypedValue(null);
  }, [roomId]);

  // Self-heal: drop a selected area that no longer belongs to the current room
  // (e.g. it was deleted from the manage panel while this dialog was open). Gate
  // on a settled, non-fetching successful load so we never clear mid-refetch —
  // that window would otherwise wipe a just-created area (whose invalidation
  // refetch is still in flight) before its cache seed is reconciled.
  useEffect(() => {
    if (!roomId) {
      if (selectedId) field.onChange('');
      return;
    }
    if (
      areasQuery.isSuccess &&
      !areasQuery.isFetching &&
      selectedId &&
      !areas.some((a) => a.id === selectedId)
    ) {
      field.onChange('');
    }
    // `field` intentionally omitted to avoid re-running on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId, areasQuery.isSuccess, areasQuery.isFetching, areas, selectedId]);

  const disabled = !roomId;
  const fieldId = `f-${name.replace(/\W+/g, '-')}`;
  const errorId = `${fieldId}-error`;

  // Show the selected area's name unless the user is actively typing a filter.
  const displayValue = typedValue ?? selectedArea?.name ?? '';
  const query = typedValue ? typedValue.trim().toLowerCase() : '';
  const filtered = query ? areas.filter((a) => a.name.toLowerCase().includes(query)) : areas;

  const selectArea = (area: RoomAreaListItem) => {
    field.onChange(area.id);
    setTypedValue(null);
    setOpen(false);
    clearErrors(name);
    window.setTimeout(() => void trigger(name), 0);
  };

  const createNew = () => {
    setOpen(false);
    onCreateNew?.();
  };

  return (
    <div className="relative">
      <Label htmlFor={fieldId}>{label}</Label>
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          id={fieldId}
          value={displayValue}
          placeholder={disabled ? t('projects.areas.selectRoomFirst') : placeholder}
          className="pl-9"
          role="combobox"
          disabled={disabled}
          aria-expanded={open}
          aria-controls={`${fieldId}-listbox`}
          aria-invalid={error ? true : undefined}
          aria-describedby={errorMessage ? errorId : undefined}
          onBlur={() => {
            field.onBlur();
            window.setTimeout(() => setOpen(false), 100);
          }}
          onFocus={() => setOpen(true)}
          onChange={(event) => {
            setTypedValue(event.currentTarget.value);
            if (selectedId) field.onChange('');
            setOpen(true);
          }}
        />
      </div>
      {open && !disabled && (
        <div
          id={`${fieldId}-listbox`}
          role="listbox"
          className="absolute z-50 mt-1 max-h-72 w-full overflow-auto rounded-md border border-border bg-popover p-1 text-sm text-popover-foreground shadow-lg"
          onMouseDown={(event) => event.preventDefault()}
        >
          {onCreateNew && (
            <button
              type="button"
              role="option"
              aria-selected={false}
              className="flex w-full items-center gap-2 rounded-sm px-3 py-2 text-left font-medium text-primary hover:bg-accent"
              onClick={createNew}
            >
              <Plus className="h-4 w-4" />
              {t('projects.areas.newArea')}
            </button>
          )}
          {areasQuery.isLoading ? (
            <div className="px-3 py-2 text-muted-foreground">{t('common.loading')}</div>
          ) : filtered.length === 0 ? (
            <div className="px-3 py-2 text-muted-foreground">{t('projects.areas.noResults')}</div>
          ) : (
            filtered.map((area) => (
              <button
                key={area.id}
                type="button"
                role="option"
                aria-selected={area.id === selectedId}
                className="block w-full rounded-sm px-3 py-2 text-left hover:bg-accent"
                onClick={() => selectArea(area)}
              >
                <span className="block font-medium">{area.name}</span>
              </button>
            ))
          )}
        </div>
      )}
      {errorMessage && (
        <p id={errorId} className="mt-1 text-xs text-destructive" role="alert">
          {errorMessage}
        </p>
      )}
    </div>
  );
}
